import {
  type ReactNode,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";

const explanations: Record<string, string> = {
  "average cost per wip": "Total material cost divided by the number of completed WIPs in the selected period.",
  "average gross profit": "Average Net Sales minus known material COGS across customers with complete cost data.",
  "average margin": "Average Gross Profit as a percentage of Net Sales for customers with complete cost data.",
  "average net sales": "Average shipped sales after closed credits across customers in the selected period.",
  "average order": "Average Net Sales per shipped order.",
  "average shipped order": "Average Net Sales per shipped order.",
  "backlog": "Open order-line value that has no recognized shipment allocation. It is separate from revenue.",
  "change": "Difference between this unit cost and the product's previous received cost in the same unit.",
  "completed receivings": "Receiving records completed within the selected date range.",
  "completed wips": "Production WIPs completed within the selected date range.",
  "cogs": "Known material cost of the inventory used in shipped products. It excludes labour and overhead.",
  "cost per output unit": "Material cost divided by finished output quantity when the output unit is compatible.",
  "credit rate": "Closed credits as a percentage of shipped sales.",
  "currency": "Transaction currency. Finance does not combine currencies or apply exchange rates.",
  "current inventory value": "Current on-hand material value across raw inventory, open WIPs, and finished goods. It is not historical.",
  "finished goods": "Current material value of finished-goods inventory on hand.",
  "gross margin": "Gross Profit divided by Net Sales, shown as a percentage.",
  "gross profit": "Net Sales minus known material COGS. Labour and overhead are not included.",
  "input / output": "Material quantity consumed compared with finished quantity produced.",
  "location": "Current storage location of the inventory.",
  "margin": "Gross Profit divided by Net Sales, shown as a percentage.",
  "material cogs": "Known material cost of inventory used in shipped products. It excludes labour and overhead.",
  "material cost": "Known cost of materials consumed by completed production WIPs.",
  "missing input costs": "Consumed production inputs that do not have a usable material cost.",
  "missing item costs": "Received items that do not have a recorded total cost.",
  "net sales": "Shipped Sales minus closed credits in the selected period.",
  "on hand": "Current quantity remaining in inventory.",
  "open wip": "Current material value held inside production WIPs that are still open.",
  "orders / shipments": "Number of distinct orders and shipment rows contributing to the result.",
  "production material cost": "Known material cost consumed by completed WIPs in the selected period.",
  "products / categories": "Distinct products and categories purchased by the customer.",
  "products / items": "Number of distinct products and received item rows from the supplier.",
  "quantity": "Quantity recorded on this shipment or receiving row, shown in its stored unit.",
  "raw / direct received": "Current material value of directly received inventory that has not been consumed into WIP.",
  "received": "Date the receiving record was completed.",
  "receivings": "Number of completed receiving records included for the supplier.",
  "receiving spend": "Total recorded cost on completed receiving items in the selected period.",
  "return / credit rate": "Closed credits as a percentage of shipped sales.",
  "sales": "Allocated shipped sales value before closed credits.",
  "shipped sales": "Sales value allocated to shipment rows in the selected period, before credits.",
  "spend": "Total recorded cost of completed receiving items from the supplier.",
  "suppliers": "Distinct suppliers with completed receiving spend in the selected period.",
  "total cost": "Recorded total cost for this received item.",
  "total inventory value": "Current known material value across raw inventory, open WIPs, and finished goods.",
  "unit cost": "Received item total cost divided by its received quantity.",
  "value": "Current known material value: on-hand quantity multiplied by its available material cost.",
  "wip": "The production work-in-progress record associated with this row.",
  "yield": "Finished output quantity divided by material input quantity. It is shown only when the units are compatible."
};

function normalize(label: string) {
  return label
    .replace(/\s*\([^)]*\)\s*$/, "")
    .trim()
    .toLowerCase();
}

interface FinanceHelpLabelProps {
  label: ReactNode;
  help?: string;
  labelVisible?: boolean;
}

interface TooltipPosition {
  left: number;
  top: number;
  placement: "above" | "below";
}

export default function FinanceHelpLabel({
  label,
  help,
  labelVisible = true,
}: FinanceHelpLabelProps) {
  const explanation =
    help ?? (typeof label === "string" ? explanations[normalize(label)] : undefined);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const tooltipId = useId();
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState<TooltipPosition | null>(null);

  const updatePosition = useCallback(() => {
    const button = buttonRef.current;
    if (!button || typeof window === "undefined") return;

    const rect = button.getBoundingClientRect();
    const halfTooltipWidth = 128;
    const viewportPadding = 12;
    const left = Math.min(
      window.innerWidth - halfTooltipWidth - viewportPadding,
      Math.max(halfTooltipWidth + viewportPadding, rect.left + rect.width / 2),
    );
    const placement = rect.top >= 96 ? "above" : "below";

    setPosition({
      left,
      top: placement === "above" ? rect.top - 8 : rect.bottom + 8,
      placement,
    });
  }, []);

  useEffect(() => {
    if (!open) return undefined;

    updatePosition();
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);

    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [open, updatePosition]);

  if (!explanation) {
    return <>{label}</>;
  }

  const showTooltip = () => {
    updatePosition();
    setOpen(true);
  };

  return (
    <span className="inline-flex items-center gap-1.5 align-middle">
      {labelVisible ? <span>{label}</span> : null}
      <button
        ref={buttonRef}
        type="button"
        className="inline-flex h-4 w-4 shrink-0 appearance-none items-center justify-center rounded-full border-0 bg-transparent p-0 text-slate-500 hover:text-emerald-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:ring-offset-1"
        aria-label={`About ${typeof label === "string" ? label : "this value"}`}
        aria-describedby={open ? tooltipId : undefined}
        onMouseEnter={showTooltip}
        onMouseLeave={() => setOpen(false)}
        onFocus={showTooltip}
        onBlur={() => setOpen(false)}
        onKeyDown={(event) => {
          if (event.key === "Escape") {
            setOpen(false);
            buttonRef.current?.blur();
          }
        }}
      >
        <svg
          aria-hidden="true"
          className="block h-4 w-4"
          viewBox="0 0 16 16"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <circle cx="8" cy="8" r="6.75" stroke="currentColor" strokeWidth="1.5" />
          <path
            d="M6.55 6.15a1.55 1.55 0 0 1 3.02.5c0 1.15-1.57 1.35-1.57 2.45"
            stroke="currentColor"
            strokeWidth="1.4"
            strokeLinecap="round"
          />
          <circle cx="8" cy="11.55" r=".75" fill="currentColor" />
        </svg>
      </button>
      {open &&
        position &&
        typeof document !== "undefined" &&
        createPortal(
          <span
            id={tooltipId}
            role="tooltip"
            className="pointer-events-none fixed z-[1000] w-64 rounded-lg bg-slate-900 px-3 py-2 text-left text-xs font-normal normal-case leading-5 tracking-normal text-white shadow-lg"
            style={{
              left: position.left,
              top: position.top,
              transform:
                position.placement === "above"
                  ? "translate(-50%, -100%)"
                  : "translate(-50%, 0)",
            }}
          >
            {explanation}
          </span>,
          document.body,
        )}
    </span>
  );
}
